import { Inject, Injectable } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivateChildFn,
  CanActivateFn,
  Router,
  RouterStateSnapshot,
} from '@angular/router';
import { Cookies } from '@bilancio/api';
import { AuthService } from '@bilancio/auth';
import { AuthActions, CoreActions, SuperStore } from '@bilancio/root-store';
import { LOGIN_PATH_CONFIG, LoginPathConfig } from '../tokens/login-path.token';
import { ONE_FRONT_BIL_CONFIG, OneFrontBilConfig } from '../tokens/one-front.token';

@Injectable({ providedIn: 'root' })
export class OneFrontBilTokenGuard {
  private retry = 5;
  private securityToken = '';

  constructor(
    private readonly srvAuth: AuthService,
    @Inject(LOGIN_PATH_CONFIG) private readonly loginPathConfig: LoginPathConfig,
    @Inject(ONE_FRONT_BIL_CONFIG) private readonly oneFrontFilConfig: OneFrontBilConfig,
    private readonly router: Router,
    private readonly store: SuperStore,
  ) {}

  canActivate: CanActivateFn = (route: ActivatedRouteSnapshot) => {
    return new Promise((resolve, reject) => {
      let tenantId = null;
      if (route?.queryParams?.ctx) {
        const ctxJson = JSON.parse(Buffer.from(route?.queryParams?.ctx, 'base64').toString('utf8'));
        tenantId = ctxJson?.tenant?.id;
        // tenantId = route?.queryParams?.tenantId;
        this.store.dispatch(CoreActions.setWorkspaceId({ workspaceId: tenantId }));
      }
      if (route?.queryParams?.workspaceId) {
        tenantId = route?.queryParams?.workspaceId;
        this.store.dispatch(CoreActions.setWorkspaceId({ workspaceId: tenantId }));
      }
      this.verifyToken()
        .then(() => {
          if (this.securityToken && !this.srvAuth.isUserLoggedIn) {
            this.srvAuth
              .doLoginDigitalWithToken(this.securityToken, tenantId)
              .then(response => {
                this.restore(route);
                resolve(response);
              })
              .catch(e => {
                this.restore();
                reject(e);
              });
          } else if (this.securityToken && this.srvAuth.isUserLoggedIn) {
            this.restore(route);
            resolve(true);
          } else {
            window.close();
            this.router.navigate([`/${this.loginPathConfig.loginPath}`]);
            this.restore();
            resolve(false);
          }
        })
        .catch(e => {
          this.restore();
          reject(e);
        });
    });
  };

  canActivateChild: CanActivateChildFn = (
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot,
  ) => {
    return this.canActivate(route, state);
  };

  private isTokenRead(): Promise<boolean> {
    return new Promise(resolve => {
      setTimeout(() => {
        this.securityToken = localStorage.getItem(this.oneFrontFilConfig.storageKey);
        this.retry--;
        resolve(this.securityToken ? true : false);
      }, 200);
    });
  }

  private async verifyToken(): Promise<boolean> {
    // eslint-disable-next-line no-async-promise-executor
    return new Promise(async resolve => {
      let val = false;
      do {
        val = await this.isTokenRead();
      } while (this.retry > 0 && !val);
      resolve(true);
    });
  }

  private restore(route: ActivatedRouteSnapshot = null) {
    this.securityToken = '';
    this.retry = 5;
    if (route?.queryParams) {
      const queryParams = route?.queryParams;
      // TODO RESTORE FOR COMIC OR EMBEDDED FE
      const isEmbedded = queryParams?.embed ? queryParams.embed === 'yes' : false;
      this.store.dispatch(CoreActions.setEmbedded({ isEmbedded }));
      const cookieConsents = queryParams?.cookieConsents
        ? queryParams.cookieConsents?.split(',')
        : [];
      const cookies: Cookies = {
        C0001: false,
        C0002: false,
        C0003: false,
        C0004: false,
        C0005: false,
        C0006: false,
        C0007: false,
      };
      for (const consent of cookieConsents) {
        if (consent in cookies) {
          cookies[consent as keyof Cookies] = true;
        }
      }
      this.store.dispatch(
        AuthActions.setDigitalCookies({
          cookies,
        }),
      );
    }
  }
}
